import {EventEmitter, Injectable} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {TaskModel, UserTasksSettings} from '../models/task.model';
import {CrmService} from './api/crm.service';
import {BoardGroupModel, BoardTableModel, MemberPermissionsModel, TaskBoardModel} from '../models/tasktable.model';
import {PaginationModel} from '../models/file-table.model';

@Injectable({
    providedIn: 'root',
})

export class TaskServiceService {
    public eminOnCreateBoardGroup: EventEmitter<BoardGroupModel> = new EventEmitter<BoardGroupModel>();
    public eminOnOpenCreateBoardModal: EventEmitter<number> = new EventEmitter<number>();

    protected tasks: TaskModel[];
    protected importanceRange: number[];
    protected colons: UserTasksSettings[];
    protected selectedBoard: TaskBoardModel;
    protected boardsRights: MemberPermissionsModel;
    protected boards: BoardTableModel[] = [];
    protected groups: BoardGroupModel[] = [];

    protected changeBoard = false;

    protected lastBoardRequestPromise: Promise<{ board: TaskBoardModel, rights: any }> = null;

    protected lastBoardsAndGroupsRequestPromise: Promise<{
        boards: BoardTableModel[],
        groups: BoardGroupModel[]
    }> = null;

    protected lastTasksRequestPromise: Promise<any> = null;

    protected tasksPromise: Promise<{ tasks: TaskModel[], importance_indexes: number[], colons: UserTasksSettings[] }>;

    constructor(
        protected crmService: CrmService,
        protected router: ActivatedRoute,
    ) {
    }

    public async getTasks(selectedBoard: TaskBoardModel, pagination: PaginationModel, reload = false): Promise<{
        tasks: TaskModel[],
        importance_indexes: number[],
        colons: UserTasksSettings[],
    }> {
        if (this.tasksPromise) {
            this.tasksPromise.finally();
            delete this.tasksPromise;
        }
        this.tasksPromise = new Promise(resolve => {
            if (!this.tasks && this.changeBoard || reload) {
                this.crmService.getTasksByBoard(selectedBoard.alias, pagination).subscribe(res => {
                    this.tasks = res.data;
                    this.importanceRange = res.importance_indexes;
                    this.colons = res.colons;
                    resolve({tasks: this.tasks, importance_indexes: res.importance_indexes, colons: res.colons});
                });
            } else {
                resolve({tasks: this.tasks, importance_indexes: this.importanceRange, colons: this.colons});
            }
        });
        return this.tasksPromise;
    }

    public async setBoard(alias: string, reload: boolean = false): Promise<{
        board: TaskBoardModel,
        rights: MemberPermissionsModel
    }> {
        if (!this.lastBoardRequestPromise || reload) {
            this.lastBoardRequestPromise = new Promise((resolve, reject) => {
                if (alias) {
                    this.crmService.getBoardByAlias(alias).subscribe({
                        next: res => {
                            this.selectedBoard = res.board;
                            this.boardsRights = res.rights;
                            this.changeBoard = true;
                            resolve(res);
                        }, error: err => {
                            reject(err);
                        },
                    });
                } else {
                    resolve(null);
                }
            });
        }
        return this.lastBoardRequestPromise;
    }

    public async getAllBoards(reload: boolean): Promise<{ boards: BoardTableModel[], groups: BoardGroupModel[] }> {
        if (!this.lastBoardsAndGroupsRequestPromise || reload) {
            this.lastBoardsAndGroupsRequestPromise = new Promise(resolve => {
                if ((this.boards.length || this.groups.length) && !reload) {
                    resolve({boards: this.boards, groups: this.groups});
                } else {
                    this.crmService.getAllBoards().subscribe(res => {
                        this.boards = res.boards;
                        this.groups = res.groups;
                        resolve(res);
                    });
                }
            });
        }
        return this.lastBoardsAndGroupsRequestPromise;
    }

    public async getBoard(alias: string, update: boolean = false): Promise<{
        board: TaskBoardModel,
        rights: MemberPermissionsModel
    }> {
        return new Promise((resolve, reject) => {
            if (this.selectedBoard && this.selectedBoard.alias === alias && !update) {
                resolve({board: this.selectedBoard, rights: this.boardsRights});
            } else {
                this.setBoard(alias, update).then(res => {
                    resolve(res);
                }).catch(err => {
                    reject(err);
                });
            }
        });
    }

    public async getTableTasks(guid: string, refresh = false, pagination: PaginationModel): Promise<any> {
        if (!this.lastTasksRequestPromise || refresh) {
            this.lastTasksRequestPromise = new Promise(resolve => {
                this.crmService.getTasks(guid, null, pagination).subscribe(res => {
                    return resolve(res);
                });
            });
        }
        return this.lastTasksRequestPromise;
    }

    public sortTasksBySearch(tasks: TaskModel[], searchText: string): TaskModel[] {
        let filteredTasks: TaskModel[];
        if (searchText) {
            filteredTasks = tasks.filter(x => {
                const fullName = x.task_alias + ' ' + x.title;
                const association = (data: string, searchFromStart = false) => {
                    if (searchFromStart) {
                        return data.toLowerCase().indexOf(searchText.toLowerCase()) === 0;
                    }
                    return data.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
                };
                if (association(fullName) || x.task_alias && association(x.title, true)) {
                    return x;
                }
            });
        } else {
            filteredTasks = tasks;
        }
        return filteredTasks;
    }
}
